import React from 'react';

import { Button, Chip, Link, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';

import { useAddVoucherToggle } from '~/hooks/apis/promo/useAddVoucherToggle';

import type { AddVoucherTogglePayload, PromoMeta } from '~/services/PromoService';

import PromoLink from '../PromoLink';

import {
  type PromoByToggleSearch,
  type PromoByToggleSearchResult,
  type ToggleSearchRelatedFeature,
  toggleSearchRelatedFeatures,
} from './PromoByToggle';

type Props = {
  search?: PromoByToggleSearch;
  promoMeta?: PromoMeta | null;
};

const PROMO_LOOKUP_PATH = '/marketing/search-promo';
const PROMO_REQUESTS_PATH = '/marketing/promo-requests';
const VOUCHER_LOOKUP_PATH = '/marketing/voucher-users';

function getRelatedFeatureLabel(entity: PromoByToggleSearchResult['entity'], feature: ToggleSearchRelatedFeature) {
  return toggleSearchRelatedFeatures[entity].find((candidate) => candidate.value === feature)?.label ?? feature;
}

function getRelatedPageUrls(entity: PromoByToggleSearchResult['entity'], result: PromoByToggleSearchResult) {
  const requestsQuery = new URLSearchParams(
    entity === 'promo' ? { codeName: result.name } : { voucherName: result.name },
  );

  if (entity === 'voucher') {
    return {
      lookup: `${VOUCHER_LOOKUP_PATH}?${new URLSearchParams({ voucher_id: result.id })}`,
      requests: `${PROMO_REQUESTS_PATH}?${requestsQuery}`,
    };
  }

  return {
    lookup: `${PROMO_LOOKUP_PATH}?${new URLSearchParams({ name: result.name, page: '1', limit: '10' })}`,
    requests: `${PROMO_REQUESTS_PATH}?${requestsQuery}`,
  };
}

function getRepairableVoucherToggles(results: Array<PromoByToggleSearchResult>, promoMeta?: PromoMeta | null) {
  const repairsByVoucherAndToggle = new Map<string, Omit<AddVoucherTogglePayload, 'brand'>>();

  for (const result of results) {
    for (const warning of result.warnings ?? []) {
      const voucherToggle = promoMeta?.voucher_toggles.find((toggle) => toggle.key === warning.missingToggle);
      if (voucherToggle) {
        repairsByVoucherAndToggle.set(`${result.id}:${voucherToggle.key}`, {
          voucherName: result.name,
          voucherToggle: voucherToggle.key,
        });
      }
    }
  }

  return [...repairsByVoucherAndToggle.values()];
}

/** Displays results returned by {@link PromoByToggle}; it is deliberately separate so callers can place it as needed. */
export default function PromoByToggleResults({ search, promoMeta }: Props) {
  const addVoucherToggleMutation = useAddVoucherToggle();

  if (!search) {
    return null;
  }

  const { displayMode, entity, results } = search;
  const repairableVoucherToggles = getRepairableVoucherToggles(
    results.filter((result) => result.entity === 'voucher'),
    promoMeta,
  );

  if (results.length === 0) {
    return (
      <Typography role="status" aria-live="polite">
        No matching {entity === 'all' ? 'promo or voucher' : entity} results found.
      </Typography>
    );
  }

  const nameColumn: GridColDef<PromoByToggleSearchResult> = {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 240,
    renderCell: (params) =>
      params.row.entity === 'promo' ? (
        <PromoLink promoId={params.row.id} title={params.row.name} copyValue={params.row.name} />
      ) : (
        <CopyableField
          value={params.row.name}
          label={
            <Link href={`/marketing/vouchers/${encodeURIComponent(params.row.id)}`} target="_blank" rel="noreferrer">
              {params.row.name}
            </Link>
          }
        />
      ),
  };

  const refinedColumns: Array<GridColDef<PromoByToggleSearchResult>> = [
    nameColumn,
    {
      field: 'entity',
      headerName: 'Type',
      minWidth: 100,
      renderCell: (params) => <Chip label={params.row.entity === 'promo' ? 'Promo code' : 'Voucher'} size="small" />,
    },
    {
      field: 'toggles',
      headerName: 'Toggles',
      sortable: false,
      filterable: false,
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const toggles = params.row.toggles;

        if (!toggles?.length) {
          return null;
        }

        const tooltipTitle = toggles.join(', ');

        return (
          <Tooltip title={tooltipTitle || undefined}>
            <Chip label={`${toggles.length} configured`} size="small" variant="outlined" />
          </Tooltip>
        );
      },
    },
    {
      field: 'relatedTo',
      headerName: 'Related features',
      sortable: false,
      filterable: false,
      flex: 1,
      minWidth: 220,
      renderCell: (params) => {
        const relatedFeatures = params.row.relatedTo ?? [];

        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ overflow: 'hidden' }}>
            {relatedFeatures.map((feature) => (
              <Chip
                key={`${feature.type}-${feature.id}`}
                label={getRelatedFeatureLabel(params.row.entity, feature.type)}
                size="small"
              />
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'warnings',
      headerName: 'Feature warnings',
      sortable: false,
      filterable: false,
      flex: 1,
      minWidth: 280,
      renderCell: (params) => {
        const warnings = params.row.warnings ?? [];

        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ overflow: 'hidden' }}>
            {warnings.map((warning) => (
              <Tooltip key={warning.missingToggle} title={warning.message}>
                <Chip label={`Missing ${warning.missingToggle}`} size="small" color="warning" />
              </Tooltip>
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'repair',
      headerName: 'Repair',
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params: { row: PromoByToggleSearchResult }) => {
        if (params.row.entity !== 'voucher') {
          return null;
        }
        const missingToggle = params.row.warnings?.[0]?.missingToggle;
        const voucherToggle = promoMeta?.voucher_toggles.find((toggle) => toggle.key === missingToggle);

        if (!voucherToggle) {
          return null;
        }

        return (
          <Button
            size="small"
            variant="text"
            disabled={addVoucherToggleMutation.isPending}
            onClick={() =>
              addVoucherToggleMutation.mutate({
                voucherName: params.row.name,
                voucherToggle: voucherToggle.key,
              })
            }
          >
            Repair
          </Button>
        );
      },
    } satisfies GridColDef<PromoByToggleSearchResult>,
  ];

  const standardColumns: Array<GridColDef<PromoByToggleSearchResult>> = [
    nameColumn,
    {
      field: 'entity',
      headerName: 'Type',
      minWidth: 100,
      renderCell: (params) => <Chip label={params.row.entity === 'promo' ? 'Promo code' : 'Voucher'} size="small" />,
    },
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
      minWidth: 280,
    },
    {
      field: 'relatedPages',
      headerName: 'Related pages',
      sortable: false,
      filterable: false,
      minWidth: 170,
      renderCell: (params) => {
        const relatedPageUrls = getRelatedPageUrls(params.row.entity, params.row);
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Link href={relatedPageUrls.lookup} target="_blank" rel="noreferrer">
              Lookup
            </Link>
            <Link href={relatedPageUrls.requests} target="_blank" rel="noreferrer">
              Requests
            </Link>
          </Stack>
        );
      },
    },
  ];

  return (
    <Stack spacing={1}>
      {displayMode === 'refined' && repairableVoucherToggles.length > 0 && (
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            disabled={addVoucherToggleMutation.isPending}
            onClick={() => {
              void Promise.allSettled(
                repairableVoucherToggles.map((repair) => addVoucherToggleMutation.mutateAsync(repair)),
              );
            }}
          >
            Repair {repairableVoucherToggles.length}
          </Button>
        </Stack>
      )}
      <DataGrid
        aria-label={`${entity} toggle search results`}
        columns={displayMode === 'refined' ? refinedColumns : standardColumns}
        rows={results}
        getRowId={(row) => `${row.entity}:${row.id}`}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        autoHeight
        density={displayMode === 'refined' ? 'compact' : 'standard'}
        {...(displayMode === 'refined'
          ? { rowHeight: 40, sx: { '& .MuiDataGrid-cell': { alignItems: 'center' } } }
          : { getRowHeight: () => 'auto', sx: { '& .MuiDataGrid-cell': { alignItems: 'start' } } })}
      />
    </Stack>
  );
}
